/**
 * 후보 비교 뷰의 데이터 모델 — 사전 점검 카드가 이미 받는 결정트리 shadow·관세율표 항목·결정례 힌트·
 * 품명 일치만으로 "후보 세번을 나란히, 질문은 후보를 가르는 필터로" 재구성한다.
 *
 * 판정 로직은 없다. 백엔드가 낸 후보·질문·배제 이유를 다른 모양으로 보여 줄 뿐이며, 답은 기존과 같은
 * 질문 키로 저장된다(예외 확인 목록의 "모두 맞습니다"도 질문별 `yes` 답 여러 개다).
 */
import { isProductQuestion, productDialogue } from "./productFactDialogue";
import { valueLabel } from "./proveFactLabels";

export const COMPARE_VIEW_PREF_KEY = "loginhs:precheck-view";

export const digitsOf = (value) => String(value ?? "").replace(/\D/g, "");

export function formatCode(value) {
  const d = digitsOf(value);
  if (d.length === 4) return d;
  if (d.length === 6) return `${d.slice(0, 4)}.${d.slice(4)}`;
  if (d.length === 10) return `${d.slice(0, 4)}.${d.slice(4, 6)}-${d.slice(6)}`;
  return String(value ?? "");
}

const YES_NO = new Set(["yes", "no"]);

function optionValues(question) {
  return (Array.isArray(question?.options) ? question.options : [])
    .map((o) => (o && typeof o === "object" ? o.value : o))
    .map((v) => String(v))
    .filter((v) => !["unknown", "undetermined"].includes(v));
}

function optionLabel(question, value) {
  const key = question.fact_key || question.key;
  const raw = (question.options || []).find((o) => o && typeof o === "object" && String(o.value) === String(value));
  return question.product_option_labels?.[value] || question.option_labels?.[value] || raw?.label || valueLabel(value, key);
}

/** 백엔드 `option_outcomes[v]` = {outcome: outside_route|route|leaf, hs6?, codes?}. 표시 힌트일 뿐 답·확정이 아니다. */
export function normalizeOutcome(raw) {
  if (!raw || typeof raw !== "object") return null;
  const outcome = ["outside_route", "route", "leaf"].includes(raw.outcome) ? raw.outcome : null;
  if (!outcome) return null;
  // review: 목록 물품이지만 엔진 경로가 이 류 밖이 아니라 전문가 검토 보류인 선택지(관측 분해 제외 확인).
  return { outcome, hs6: (Array.isArray(raw.hs6) ? raw.hs6 : []).map(digitsOf).filter(Boolean), codes: (Array.isArray(raw.codes) ? raw.codes : []).map(digitsOf).filter(Boolean), ...(raw.review === true ? { review: true } : {}) };
}

/** 선택지 결과가 특정 후보 코드로 가는가: leads(그 후보) · other(다른 후보) · outside(계약 밖) · null(정보 없음). */
export function outcomeRelation(outcome, candidateCode) {
  if (!outcome) return null;
  if (outcome.outcome === "outside_route") return "outside";
  const code = digitsOf(candidateCode);
  if (!code) return null;
  // 보류로 가는 목록 물품 가지(review)는 어떤 후보도 '이끄는' 것으로 강조하지 않는다.
  if (outcome.review) return "review";
  if (outcome.codes.length) return outcome.codes.some((c) => c === code || c.startsWith(code) || code.startsWith(c)) ? "leads" : "other";
  if (outcome.hs6.length) return outcome.hs6.some((h) => code.startsWith(h) || h.startsWith(code)) ? "leads" : "other";
  return null;
}

/** 질문을 세 묶음으로 나눈다: 확인 목록(예/아니오), 구분 질문(선택지), 설명 입력(자유 입력). */
export function groupQuestions(tree, answers = {}) {
  const dialogue = productDialogue(tree);
  const all = [...(dialogue.questions || []), ...(dialogue.deferred_questions || [])]
    .filter(isProductQuestion)
    .filter((q) => q && (q.fact_key || q.key));
  const seen = new Set();
  const checklist = [];
  const choices = [];
  const inputs = [];
  for (const question of all) {
    const key = question.fact_key || question.key;
    if (seen.has(key)) continue;
    seen.add(key);
    const values = optionValues(question);
    const answer = answers[key];
    const base = {
      key,
      prompt: question.prompt_ko || question.question || question.label || key,
      hint: question.why_asked || question.hint || null,
      answer: answer === undefined ? undefined : String(answer),
      allowUnknown: question.allow_unknown !== false,
    };
    if (values.length === 2 && values.every((v) => YES_NO.has(v))) {
      const outcomes = question.option_outcomes && typeof question.option_outcomes === "object" ? question.option_outcomes : null;
      checklist.push({ ...base, kind: "check",
        yesOutcome: normalizeOutcome(outcomes?.yes), noOutcome: normalizeOutcome(outcomes?.no),
        state: answer === undefined ? "open" : String(answer) === "yes" ? "ok" : String(answer) === "no" ? "flag" : "unknown" });
    } else if (values.length > 0) {
      const outcomes = question.option_outcomes && typeof question.option_outcomes === "object" ? question.option_outcomes : null;
      choices.push({ ...base, kind: "choice",
        options: values.map((v) => ({ value: v, label: optionLabel(question, v), outcome: normalizeOutcome(outcomes?.[v]) })) });
    } else {
      inputs.push({ ...base, kind: "input" });
    }
  }
  return { status: dialogue.status, title: dialogue.title, checklist, choices, inputs, documentRequests: dialogue.document_requests || [] };
}

/**
 * 류 공통 트리의 호 선택 질문(`chapter{NN}_article_family`). 상품 질문 필터(`isProductQuestion`)는 이 키를 법적 질문으로 걸러
 * 대화에는 없지만, 지도에서는 "호를 가르는 사실"이다. 옵션 값은 family 키, 라벨은 "9101 · 호 명칭" — 호는 라벨 앞 4자리로 읽는다.
 */
/** 56차: 트리가 내려준 호·소호 라벨에 CLIP 원문의 <a onClick=…> 태그가 섞여 화면에 그대로 찍히던 것 — 태그를 벗기고 공백을 모은다. */
export function plainLabel(raw) {
  return String(raw ?? "").replace(/<[^>]*>/g, "").replace(/\s+/g, " ").trim();
}

export function headingFamilyQuestion(tree) {
  const qs = Array.isArray(tree?.questions) ? tree.questions : [];
  const q = qs.find((x) => x && /^chapter\d+_article_family$/.test(String(x.fact_key || x.key || "")));
  if (!q) return null;
  const labels = q.option_labels && typeof q.option_labels === "object" ? q.option_labels : {};
  const values = (Array.isArray(q.options) ? q.options : []).map((o) => (o && typeof o === "object" ? o.value : o)).map(String);
  const options = values.map((v) => { const label = plainLabel(labels[v] || v); const m = label.match(/^\s*(\d{4})\b/); return { value: v, label, hs4: m ? m[1] : null }; });
  const names = {}; options.forEach((o) => { if (o.hs4) names[o.hs4] = o.label.replace(/^\s*\d{4}\s*[·:-]?\s*/, "").trim(); });
  return { key: q.fact_key || q.key, prompt: q.prompt_ko || q.question || q.label || "물품의 종류(호)", options, hs4s: options.map((o) => o.hs4).filter(Boolean),
    recommended: (Array.isArray(q.recommended_options) ? q.recommended_options : []).map(String), names };
}

/** 소호 선택 질문(`chapter{NN}_subheading_family`) — 라벨 "9102.11 · …"의 앞 6자리가 소호. */
export function subheadingFamilyQuestion(tree) {
  const qs = Array.isArray(tree?.questions) ? tree.questions : [];
  const q = qs.find((x) => x && /^chapter\d+_subheading_family$/.test(String(x.fact_key || x.key || "")));
  if (!q) return null;
  const labels = q.option_labels && typeof q.option_labels === "object" ? q.option_labels : {};
  const values = (Array.isArray(q.options) ? q.options : []).map((o) => (o && typeof o === "object" ? o.value : o)).map(String);
  const options = values.map((v) => { const label = plainLabel(labels[v] || v); const m = label.match(/^\s*(\d{4})\.(\d{2})\b/); return { value: v, label, hs6: m ? m[1] + m[2] : null }; });
  return { key: q.fact_key || q.key, prompt: q.prompt_ko || q.question || q.label || "소호", options, recommended: (Array.isArray(q.recommended_options) ? q.recommended_options : []).map(String) };
}

export function scopeHs4(tree) {
  if (tree?.supported_hs4) return String(tree.supported_hs4);
  if (Array.isArray(tree?.hs4_candidates) && tree.hs4_candidates.length === 1) return String(tree.hs4_candidates[0]);
  const conditional = tree?.product_fact_dialogue?.conditional_assessment?.candidates;
  if (Array.isArray(conditional) && conditional.length) {
    const codes = [...new Set(conditional.map((c) => digitsOf(c.code).slice(0, 4)).filter(Boolean))];
    if (codes.length === 1) return codes[0];
  }
  return null;
}

function precedentCount(hints, code) {
  const rows = Array.isArray(hints?.by_hsk) ? hints.by_hsk : [];
  const d = digitsOf(code);
  return rows.filter((r) => digitsOf(r?.hsk).startsWith(d)).reduce((sum, r) => sum + Number(r.count || 0), 0);
}

function latestPrecedent(hints, code) {
  const rows = Array.isArray(hints?.by_hsk) ? hints.by_hsk : [];
  const d = digitsOf(code);
  return rows.filter((r) => digitsOf(r?.hsk).startsWith(d)).map((r) => r.latest_date).filter(Boolean).sort().pop() || null;
}

function leafFor(items, hsk) {
  const d = digitsOf(hsk);
  return (Array.isArray(items) ? items : []).find((it) => digitsOf(it?.hsk) === d) || null;
}

function rateOf(item) {
  const rates = item?.rates || {};
  const basic = rates.A != null ? String(rates.A) : null;
  // FTA 협정세율(F로 시작)은 수십 개라 카드에서는 뺀다. 기본 외 일반 세율(양허 C, 탄력 E·P·W 등) 두 개만.
  const others = Object.entries(rates).filter(([code]) => code !== "A" && !/^F/.test(code)).map(([code, val]) => `${code} ${val ?? ""}`.trim());
  return { basic, others: others.slice(0, 2), more: Math.max(0, others.length - 2) };
}

function parseRejected(text) {
  const m = String(text || "").match(/^\s*(\d{4,10})\s*[:：]\s*(.*)$/);
  return m ? { code: m[1], reason: m[2].trim() } : null;
}

/**
 * 후보 목록. 현재 경로(path) → 열린 형제(open) → 조건부(conditional) → 배제(excluded) 순.
 * 배제 후보는 트리가 범위 밖으로 둔 호·철회한 방향·트리 범위 밖의 품명 일치다.
 */
/** 제안 카드 응답의 우리 사무소 이력(`signals.history.by_code[]`)에서 이 코드(접두 일치)의 독립 신고 수. 없으면 null(연결 전과 구분). */
export function proposalHistoryCount(proposal, code) {
  const rows = proposal?.signals?.history?.by_code;
  if (!Array.isArray(rows)) return null;
  const d = digitsOf(code);
  return rows.filter((r) => { const h = digitsOf(r?.hs_code); return h && (h.startsWith(d) || d.startsWith(h)); }).reduce((n, r) => n + Number(r.declarations ?? r.lines ?? 0), 0);
}

/** 제안 카드 응답의 LLM 후보 중 이 코드와 접두가 겹치는 것. */
export function proposalLlmHit(proposal, code) {
  const cands = proposal?.signals?.llm?.candidates;
  if (!Array.isArray(cands)) return null;
  const d = digitsOf(code);
  const hit = cands.find((c) => { const h = digitsOf(c?.code); return h && (h.startsWith(d) || d.startsWith(h)); });
  return hit ? { code: digitsOf(hit.code), why: String(hit.why || "") } : null;
}

/** 제안 카드의 LLM 후보를 분기 지도 `llmMap` 모양으로 — 트리가 모르는 호를 점선 마디로 그리는 데 쓴다(proposal/map 대체). */
export function proposalLlmMap(proposal) {
  const cands = proposal?.signals?.llm?.candidates;
  if (!Array.isArray(cands) || !cands.length) return null;
  return { candidates: cands.map((c) => ({ code: digitsOf(c?.code), level: c?.level || null, name: String(c?.why || ""), condition: String(c?.why || "") })).filter((c) => c.code.length >= 4), source: "proposal_card" };
}

/**
 * 트리가 후보를 하나도 못 냈을 때의 후보 — 제안 카드의 네 신호에서 온다(LLM 후보·결정례 최다 세번·관세율표 명칭 일치·우리 사무소 이력).
 * 전부 `state:"open"`·`source:"proposal"`이며 확정이 아니다. 어떤 신호가 데려왔는지는 `origin`에 남긴다.
 */
export function buildProposalCandidates({ proposal, tariffItems, tariffMatches, precedentHints }) {
  const items = Array.isArray(tariffItems) ? tariffItems : [];
  const signals = proposal?.signals || {};
  const hints = signals.precedent?.status === "ok" && signals.precedent.hints ? signals.precedent.hints : precedentHints;
  const out = new Map();
  const add = (code, origin, extra = {}) => {
    const d = digitsOf(code);
    if (!d || ![4, 6, 10].includes(d.length)) return;
    const cur = out.get(d) || { code: d, state: "open", source: "proposal", level: d.length === 4 ? "HS4" : d.length === 6 ? "HS6" : "KR10", origin: {} };
    cur.origin[origin] = true;
    Object.assign(cur, extra);
    out.set(d, cur);
  };
  (Array.isArray(signals.llm?.candidates) ? signals.llm.candidates : []).forEach((c) => add(c.code, "llm", { llmWhy: String(c.why || "") }));
  (Array.isArray(hints?.by_hsk) ? [...hints.by_hsk].sort((a, b) => Number(b.count || 0) - Number(a.count || 0)).slice(0, 5) : []).forEach((r) => add(r.hsk, "precedent", { precedentLabel: r.label || null }));
  (Array.isArray(signals.history?.by_code) ? signals.history.by_code : []).forEach((r) => add(r.hs_code, "history"));
  (Array.isArray(tariffMatches) ? tariffMatches : []).forEach((m) => add(m.hsk, "tariff", { nameMatch: m.qualified ? "qualified" : "exact", nameVia: nameViaOf(m), name: m.ko }));
  if (out.size === 0) return [];
  return [...out.values()].map((c) => {
    const leaf = c.code.length === 10 ? leafFor(items, c.code) : null;
    const rate = rateOf(leaf);
    const name = c.name || leaf?.desc_ko?.replace(/\/\s*양허유형.*$/u, "").trim() || c.precedentLabel || c.llmWhy || null;
    const precedents = precedentCount(hints, c.code);
    const history = proposalHistoryCount(proposal, c.code);
    const llm = proposalLlmHit(proposal, c.code);
    const evidence = [precedents > 0, c.nameMatch === "exact", (history || 0) > 0, Boolean(llm)];
    return { ...c, display: formatCode(c.code), name, nameEn: leaf?.desc || null, rate: leaf ? rate : null, importReq: leaf ? Boolean(leaf.has_import_req) : null,
      precedents, precedentLatest: latestPrecedent(hints, c.code), history, llm, pending: null, support: evidence.filter(Boolean).length, supportOf: evidence.length };
  }).sort((a, b) => b.support - a.support || b.code.length - a.code.length || b.precedents - a.precedents); // 근거 수 → 더 구체적인 세번 → 결정례 수
}

/**
 * 약한 깨우기(2026-09-15) — 트리가 글자만으로 깨어나 묻기만 하고(ask) 후보·사실이 없으며, 제안 신호의 호가 전부 그 류 밖이면
 * 그 트리의 류(2자리)를 돌려준다(서버 라우터와 같은 판단; 카드는 이때 힌트를 실어 한 번 더 계산한다). 아니면 "".
 */
export function weakWakeChapter(tree, hints) {
  if (!tree || tree.action !== "ask" || tree.routed_by_candidate) return "";
  if (tree.supported_hs4 || tree.supported_hs6 || tree.supported_hsk) return "";
  if ((Array.isArray(tree.hs4_candidates) && tree.hs4_candidates.length) || (Array.isArray(tree.hs6_candidates) && tree.hs6_candidates.length)) return "";
  const facts = tree.facts;
  if (Array.isArray(facts)) { if (facts.length) return ""; }
  else if (facts && typeof facts === "object") { if (Object.keys(facts).some((k) => !k.startsWith("_"))) return ""; }
  const src = `${tree.source || ""}|${tree.output_field || ""}`;
  const m = src.match(/chapter(\d{2})/);
  const chapter = m ? m[1] : (src.includes("machinery") ? "84" : String(tree.chapter || "").replace(/\D/g, "").slice(0, 2));
  if (!chapter) return "";
  const list = Array.isArray(hints) ? hints : [];
  if (!list.length || list.some((h) => String(h?.hs4 || h || "").startsWith(chapter))) return "";
  return chapter;
}

/** 명칭 일치의 출처 — exact(글자) · modifier(끝 낱말) · synonym(엔진 동의어) · llm(LLM이 읽은 품명). 배지·자동 길 문구용. */
export function nameViaOf(m) {
  if (!m) return "exact";
  if (m.llm) return "llm";
  if (m.synonym) return "synonym";
  if (m.modifier) return "modifier";
  return "exact";
}

export function buildCandidates({ tree, tariffItems, tariffMatches, precedentHints, pendingCount, proposal = null }) {
  const items = Array.isArray(tariffItems) ? tariffItems : [];
  const conditional = tree?.product_fact_dialogue?.conditional_assessment;
  const hs4 = scopeHs4(tree);
  const out = new Map();
  const add = (code, state, extra = {}) => {
    const d = digitsOf(code);
    if (!d || d.length < 4) return;
    const existing = out.get(d);
    if (existing) { Object.assign(existing, { ...extra, state: existing.state === "path" ? "path" : state }); return; }
    out.set(d, { code: d, state, ...extra });
  };

  const supportedHsk = digitsOf(tree?.supported_hsk);
  const supportedHs6 = digitsOf(tree?.supported_hs6);
  const active = Array.isArray(tree?.active_hsk_candidates) ? tree.active_hsk_candidates.map(digitsOf).filter(Boolean) : [];
  // 소호까지 왔고 10자리가 아직이면, 검색된 관세율표 잎을 열린 후보로 펼친다(6개 이하일 때). 없으면 소호 하나로 둔다.
  const leavesUnder = (hs6) => items.map((it) => digitsOf(it?.hsk)).filter((d) => d.length === 10 && d.startsWith(hs6));
  const addHs6 = (hs6, hs6State, extra = {}) => {
    const leaves = leavesUnder(hs6);
    if (leaves.length > 0 && leaves.length <= 6) leaves.forEach((c) => add(c, "open", { level: "KR10", ...extra }));
    else add(hs6, hs6State, { level: "HS6", ...extra });
  };
  if (supportedHsk.length === 10) add(supportedHsk, "path", { level: "KR10" });
  else if (active.length) active.forEach((c) => add(c, "open", { level: "KR10" }));
  else if (supportedHs6.length === 6) addHs6(supportedHs6, "path");
  else if (Array.isArray(tree?.hs6_candidates) && tree.hs6_candidates.length) {
    tree.hs6_candidates.forEach((c) => add(c, "open", { level: "HS6" }));
  }
  if (Array.isArray(conditional?.candidates)) {
    for (const c of conditional.candidates) {
      const d = digitsOf(c.code);
      const covered = [...out.keys()].some((k) => k.startsWith(d) && k !== d);
      if (covered || out.has(d)) { // 같은 방향의 후보가 이미 있으면 이유만 넘긴다
        [...out.values()].filter((v) => v.code.startsWith(d)).forEach((v) => { v.reason = v.reason || c.reason; v.remaining = v.remaining || c.remaining; });
      } else if (d.length === 6) addHs6(d, out.size ? "open" : "conditional", { reason: c.reason, remaining: c.remaining });
      else add(d, out.size ? "open" : "conditional", { level: c.level || (d.length === 4 ? "HS4" : "HS6"), reason: c.reason, remaining: c.remaining });
    }
  }
  // 호 후보만 여럿인 경우(류 트리 `ambiguous`): 각 호를 미확정 후보로 둔다. 호 선택 질문의 라벨("9101 · 호명")에서도 뽑는다.
  if (out.size === 0) {
    const hs4s = Array.isArray(tree?.hs4_candidates) ? tree.hs4_candidates.map(digitsOf).filter((d) => d.length === 4) : [];
    const fromQuestion = headingFamilyQuestion(tree)?.hs4s || [];
    const heads = [...new Set([...hs4s, ...fromQuestion])];
    if (heads.length >= 2) heads.forEach((h) => add(h, "open", { level: "HS4", fromHeadingChoice: true }));
  }

  // 배제·철회
  const excluded = [];
  const pushExcluded = (code, reason, extra = {}) => {
    const d = digitsOf(code);
    if (!d || out.has(d) || excluded.some((e) => e.code === d)) return;
    excluded.push({ code: d, state: "excluded", level: d.length === 4 ? "HS4" : d.length === 6 ? "HS6" : "KR10", reason, ...extra });
  };
  if (tree?.outside_candidate_hs6) pushExcluded(tree.outside_candidate_hs6, "결정트리가 이 류 밖의 후보로 두었습니다. 위 확인 답에 따라 갈립니다.");
  else if (tree?.outside_candidate_hs4) pushExcluded(tree.outside_candidate_hs4, "결정트리가 이 류 밖의 후보로 두었습니다. 위 확인 답에 따라 갈립니다.");
  (Array.isArray(tree?.outside_chapter_candidates) ? tree.outside_chapter_candidates : [])
    .forEach((c) => pushExcluded(c, "결정트리가 범위 밖 후보로 두었습니다."));
  (Array.isArray(conditional?.rejected_directions) ? conditional.rejected_directions : [])
    .map(parseRejected).filter(Boolean).forEach((r) => pushExcluded(r.code, r.reason, { withdrawn: true }));
  // 방향이 전부 철회되면 conditional_assessment 가 없고 product_intake.withdrawal_reasons 만 남는다 — 그 호를 배제 후보로 둔다.
  (Array.isArray(tree?.product_fact_dialogue?.product_intake?.withdrawal_reasons) ? tree.product_fact_dialogue.product_intake.withdrawal_reasons : [])
    .map(parseRejected).filter(Boolean).forEach((r) => pushExcluded(r.code, r.reason, { withdrawn: true }));
  const sibling = Array.isArray(tree?.observable_derivation?.sibling_exclusion_evaluations) ? tree.observable_derivation.sibling_exclusion_evaluations : [];
  sibling.filter((s) => s?.excluded).forEach((s) => pushExcluded(s.hs6, s.reason));

  // 품명 일치: 범위 안이면 표시, 밖이면 배제 카드로
  const matches = Array.isArray(tariffMatches) ? tariffMatches : [];
  for (const m of matches) {
    const d = digitsOf(m.hsk);
    const inScope = hs4 ? d.startsWith(hs4) : false;
    if (out.has(d)) { out.get(d).nameMatch = m.qualified ? "qualified" : "exact"; out.get(d).nameVia = nameViaOf(m); }
    else if (inScope) add(d, "open", { level: "KR10", nameMatch: m.qualified ? "qualified" : "exact", nameVia: nameViaOf(m) });
    else {
      const ex = excluded.find((e) => d.startsWith(e.code));
      if (ex && ex.code !== d) { ex.code = d; ex.level = "KR10"; }
      const target = excluded.find((e) => e.code === d);
      if (target) { target.nameMatch = m.qualified ? "qualified" : "exact"; target.nameVia = nameViaOf(m); target.name = m.ko; target.reason = target.reason || `이름은 같지만 제${m.hs4}호의 조건을 충족할 때만 이 세번입니다.`; }
      else pushExcluded(d, `이름은 같지만 제${m.hs4}호의 조건을 충족할 때만 이 세번입니다. 결정트리는 지금 ${hs4 ? `제${hs4}호` : "다른 호"}를 보고 있습니다.`, { nameMatch: m.qualified ? "qualified" : "exact", name: m.ko });
    }
  }

  if (out.size === 0 && excluded.length === 0) return [];
  const decorate = (c) => {
    const leaf = c.code.length === 10 ? leafFor(items, c.code) : null;
    const rate = rateOf(leaf);
    const name = c.name || leaf?.desc_ko?.replace(/\/\s*양허유형.*$/u, "").trim() || (c.code === hs4 && tree?.hs4_label_ko) || headingFamilyQuestion(tree)?.names?.[c.code] || null;
    const precedents = precedentCount(precedentHints, c.code);
    const evidence = [precedents > 0, c.nameMatch === "exact", Boolean(leaf)];
    return {
      ...c,
      display: formatCode(c.code),
      name,
      nameEn: leaf?.desc || null,
      rate: leaf ? rate : null,
      importReq: leaf ? Boolean(leaf.has_import_req) : null,
      precedents,
      precedentLatest: latestPrecedent(precedentHints, c.code),
      history: proposalHistoryCount(proposal, c.code), // 제안 카드 응답(우리 사무소 독립 신고 수) — 없으면 null
      llm: proposalLlmHit(proposal, c.code), // 제안 카드의 LLM 후보와 겹치면 배지(제안일 뿐)
      pending: c.state === "excluded" ? null : pendingCount,
      support: evidence.filter(Boolean).length,
      supportOf: evidence.length,
    };
  };
  const live = [...out.values()].map(decorate);
  const best = live.reduce((m, c) => Math.max(m, c.support), 0);
  const leaders = live.filter((c) => c.support === best && best > 0);
  if (leaders.length === 1) leaders[0].lead = true;
  return [...live, ...excluded.map(decorate)];
}

/** 관세율표 원문 표 — 현재 호(scope)의 검색된 잎을 상태(path/open/excluded)와 이유로 칠한다. */
/** `/api/onto/heading-detail?hs=NNNN&lang=kor` 응답을 관세율표 항목 모양으로 바꾼다(검색 결과에 그 호의 잎이 없을 때 보충). */
export function headingDetailToItems(detail) {
  const rows = Array.isArray(detail?.rows) ? detail.rows : [];
  const items = rows.filter((r) => digitsOf(r?.hsk).length === 10).map((r) => ({
    hsk: digitsOf(r.hsk), hs4: String(r.hs4 || digitsOf(r.hsk).slice(0, 4)), chapter: String(r.chapter || digitsOf(r.hsk).slice(0, 2)),
    desc_ko: r.name_ko || "", desc: r.popum || "", rates: r.rates || {}, has_import_req: r.has_import_req, from_heading_detail: true,
  }));
  const hs6Labels = detail?.subheading_popums_ko && typeof detail.subheading_popums_ko === "object" ? detail.subheading_popums_ko : {};
  return { hs4: detail?.hs4 ? String(detail.hs4) : null, items, hs6Labels, headingLabel: detail?.heading_title_ko || null };
}

export function buildSheet({ tree, tariffItems, candidates, hs6Labels = {}, headingLabel = null }) {
  const hs4 = scopeHs4(tree);
  const items = (Array.isArray(tariffItems) ? tariffItems : []).filter((it) => digitsOf(it?.hsk).length === 10 && digitsOf(it.hsk).startsWith(hs4 || "----"));
  if (!hs4 || items.length === 0) return { hs4, rows: [], reason: hs4 ? "search_items_missing" : "no_heading" };
  const byCode = new Map((candidates || []).map((c) => [c.code, c]));
  const supportedHs6 = digitsOf(tree?.supported_hs6);
  const conditional = tree?.product_fact_dialogue?.conditional_assessment?.candidates;
  const condHs6 = Array.isArray(conditional) ? conditional.map((c) => digitsOf(c.code)).filter((d) => d.length === 6) : [];
  const currentHs6 = supportedHs6 || (condHs6.length === 1 ? condHs6[0] : "");
  const siblingReasons = new Map((Array.isArray(tree?.observable_derivation?.sibling_exclusion_evaluations) ? tree.observable_derivation.sibling_exclusion_evaluations : [])
    .filter((s) => s?.excluded).map((s) => [digitsOf(s.hs6), s.reason]));
  const rows = [];
  let lastHs6 = null;
  for (const it of [...items].sort((a, b) => digitsOf(a.hsk).localeCompare(digitsOf(b.hsk)))) {
    const hsk = digitsOf(it.hsk);
    const hs6 = hsk.slice(0, 6);
    if (hs6 !== lastHs6) {
      lastHs6 = hs6;
      let state = "open";
      let why = null;
      if (currentHs6 && hs6 === currentHs6) state = "path";
      else if (currentHs6) { state = "excluded"; why = siblingReasons.get(hs6) || "현재 경로의 형제 소호 · 확인한 겉감·구조 조건이 가리키는 소호가 아닙니다."; }
      rows.push({ kind: "hs6", code: hs6, display: formatCode(hs6), state, why, label: hs6Labels[hs6] ? String(hs6Labels[hs6]).replace(/^[\d.\s-]+/, "").trim() : null });
    }
    const cand = byCode.get(hsk);
    let state = "open";
    let why = null;
    if (cand) state = cand.state === "path" ? "path" : cand.state === "excluded" ? "excluded" : "open";
    else if (currentHs6 && hs6 !== currentHs6) { state = "excluded"; }
    else if (currentHs6 && byCode.size && ![...byCode.values()].some((c) => c.code === hsk)) state = "excluded";
    if (state === "excluded" && cand?.reason) why = cand.reason;
    const rate = rateOf(it);
    rows.push({ kind: "leaf", code: hsk, display: hsk.slice(6), hs6, ko: String(it.desc_ko || "").replace(/\/\s*양허유형.*$/u, "").trim(), en: it.desc || "",
      basic: rate.basic, others: rate.others, state, why, candidate: cand || null });
  }
  return { hs4, rows, reason: null, headingLabel: headingLabel || tree?.hs4_label_ko || null };
}

/** 호 진입 게이트의 원문 근거 — 조건부 방향의 sources 또는 도출 기록의 exclusion_basis. */
export function gateSources(tree) {
  const fromConditional = tree?.product_fact_dialogue?.conditional_assessment?.sources;
  const fromDerivation = tree?.observable_derivation?.exclusion_basis;
  const list = Array.isArray(fromConditional) && fromConditional.length ? fromConditional : Array.isArray(fromDerivation) ? fromDerivation : [];
  return list.map((s) => ({
    title: s?.title || s?.source_ref || "원문",
    url: (() => { try { const u = new URL(s?.url || s?.source_url); return u.protocol === "https:" ? u.href : null; } catch { return null; } })(),
    excerpt: s?.excerpt || s?.source_excerpt || null,
  })).slice(0, 6);
}

/** 검색 결과에 현재 호의 10자리 잎이 하나도 없으면 호 상세를 한 번 더 불러 표와 후보를 채운다. */
export function needsHeadingDetail(tree, tariffItems) {
  const hs4 = scopeHs4(tree);
  if (!hs4) return null;
  const has = (Array.isArray(tariffItems) ? tariffItems : []).some((it) => digitsOf(it?.hsk).length === 10 && digitsOf(it.hsk).startsWith(hs4));
  return has ? null : hs4;
}

const EMPTY_GROUPS = Object.freeze({ status: null, title: null, checklist: [], choices: [], inputs: [], documentRequests: [] });

/** 트리 후보가 없을 때 제안 카드 신호만으로 만드는 모델(`mode:"proposal_only"`). 트리가 없어도 된다. */
function proposalOnlyModel({ tree, groups, tariffItems, tariffMatches, precedentHints, proposal }) {
  const candidates = buildProposalCandidates({ proposal, tariffItems, tariffMatches, precedentHints });
  if (!candidates.length) return null;
  return { available: true, mode: "proposal_only", reviewFocus: "", hs4: null, groups: groups || EMPTY_GROUPS, pendingCount: 0, candidates,
    sheet: { hs4: null, rows: [], reason: "proposal_only", headingLabel: null }, sources: [], ready: [], treePresent: Boolean(tree) };
}

export function buildCandidateCompareModel({ tree, answers = {}, tariffItems, tariffMatches, precedentHints, headingDetail = null, proposal = null }) {
  if (!tree || typeof tree !== "object") {
    // 트리 자체가 없어도(류 후보 없음) 제안 카드 신호가 있으면 지도를 그린다. 전부 제안·미확정.
    return proposalOnlyModel({ tree: null, groups: null, tariffItems, tariffMatches, precedentHints, proposal }) || { available: false, reason: "no_tree" };
  }
  const detail = headingDetail && headingDetail.hs4 && headingDetail.hs4 === scopeHs4(tree) ? headingDetail : null;
  if (detail && detail.items.length) {
    const seen = new Set((Array.isArray(tariffItems) ? tariffItems : []).map((it) => digitsOf(it?.hsk)));
    tariffItems = [...(Array.isArray(tariffItems) ? tariffItems : []), ...detail.items.filter((it) => !seen.has(it.hsk))];
  }
  const groups = groupQuestions(tree, answers);
  const pendingCount = groups.checklist.filter((c) => c.state === "open").length
    + groups.choices.filter((c) => c.answer === undefined).length
    + groups.inputs.filter((c) => c.answer === undefined).length;
  const candidates = buildCandidates({ tree, tariffItems, tariffMatches, precedentHints, pendingCount, proposal });
  const liveCount = candidates.filter((c) => c.state !== "excluded").length;
  // 살아 있는 후보가 없어도 철회된 방향이 있으면 지도는 그린다 — 무엇 때문에 빠졌는지가 곧 다음 행동이다.
  const withdrawn = liveCount === 0 && candidates.some((c) => c.withdrawn);
  if (liveCount === 0 && !withdrawn) {
    // 트리가 후보를 못 냈다 → 제안 카드 신호로 그린다(없으면 종전처럼 질문 목록으로).
    return proposalOnlyModel({ tree, groups, tariffItems, tariffMatches, precedentHints, proposal }) || { available: false, reason: "no_candidates", groups };
  }
  attachBlockingFacts(candidates, tree, answers);
  markUnreachable(candidates, groups);
  const sheet = buildSheet({ tree, tariffItems, candidates, hs6Labels: detail?.hs6Labels || {}, headingLabel: detail?.headingLabel || null });
  return {
    available: true,
    mode: withdrawn ? "withdrawn" : "live",
    reviewFocus: withdrawn ? String(tree?.product_fact_dialogue?.product_intake?.review_focus || "") : "",
    hs4: sheet.hs4,
    groups,
    pendingCount,
    candidates,
    sheet,
    sources: gateSources(tree),
    ready: candidates.filter((c) => c.state !== "excluded" && c.pending === 0 && c.code.length === 10 && (c.state === "path" || candidates.filter((x) => x.state !== "excluded").length === 1)),
  };
}

/**
 * 어떤 선택지도 데려가지 않는 살아 있는 KR10 후보를 표시한다(예: 6210.10-1000 펠트 — 이 계약은 펠트를 범위 밖으로 둔다).
 * 같은 소호 안에서 다른 잎으로 가는 선택지가 하나라도 있을 때만 판단한다. 배제가 아니라 "이 계약이 데려가지 않음"이다.
 */
export function markUnreachable(candidates, groups) {
  const reachable = new Set();
  let known = false;
  for (const item of [...(groups?.choices || []), ...(groups?.checklist || [])]) {
    const outcomes = item.kind === "choice" ? item.options.map((o) => o.outcome) : [item.yesOutcome, item.noOutcome];
    for (const o of outcomes) { if (o && o.codes?.length) { known = true; o.codes.forEach((c) => reachable.add(c)); } }
  }
  if (!known) return candidates;
  for (const c of candidates) {
    if (c.state === "excluded" || c.code.length !== 10) continue;
    const sameHs6Reachable = [...reachable].some((r) => r.slice(0, 6) === c.code.slice(0, 6));
    if (sameHs6Reachable && !reachable.has(c.code)) c.unreachable = true;
  }
  return candidates;
}

/**
 * 배제된 후보를 막은 사용자 답. 철회 문구("겉감의 재료와 구조는 무엇인가요? — 부직포 (이 경로의 조건과 다름)")에
 * 확인된 사실의 질문 문구나 값 문구가 들어 있으면 그 사실을 잡는다. 못 찾으면 사용자가 직접 확인한 방향 사실 전부.
 * 지우면 트리가 그 질문을 다시 묻고 재계산한다 — 후보를 대신 골라 주는 것이 아니다.
 */
export function attachBlockingFacts(candidates, tree, answers = {}) {
  const assumptions = Array.isArray(tree?.product_fact_dialogue?.conditional_assessment?.assumptions)
    ? tree.product_fact_dialogue.conditional_assessment.assumptions : [];
  const confirmed = assumptions
    .filter((a) => a && a.fact_key && ["user_confirmed_product_fact", "input_description"].includes(a.origin))
    .map((a) => ({ key: a.fact_key, label: a.label || a.fact_key, value: a.value_label || String(answers[a.fact_key] ?? "") }));
  const treeFacts = Array.isArray(tree?.facts) ? tree.facts : [];
  for (const f of treeFacts) {
    if (f && f.key && f.status === "user_confirmed" && !confirmed.some((c) => c.key === f.key) && answers[f.key] !== undefined) {
      confirmed.push({ key: f.key, label: f.key, value: String(f.value ?? "") });
    }
  }
  for (const c of candidates) {
    if (c.state !== "excluded") continue;
    const reason = String(c.reason || "");
    let hits = confirmed.filter((f) => (f.label && reason.includes(f.label)) || (f.value && f.value.length >= 2 && reason.includes(f.value)));
    if (!hits.length && c.withdrawn) hits = confirmed.filter((f) => String(f.key).startsWith("direction_"));
    c.blockingFacts = hits.filter((f) => answers[f.key] !== undefined);
  }
  return candidates;
}

const VIEW_MODES = ["map", "table", "questions"];
/** 기본은 지도(2026-09-13 사용자 결정). 저장된 선택이 있으면 그것. 옛 "compare"(후보 비교)는 지도로 승계한다. */
export function readViewPref() {
  try { const v = localStorage.getItem(COMPARE_VIEW_PREF_KEY); if (v === "compare") return "map"; return VIEW_MODES.includes(v) ? v : "map"; } catch (_) { return "map"; }
}
export function writeViewPref(mode) {
  try { localStorage.setItem(COMPARE_VIEW_PREF_KEY, VIEW_MODES.includes(mode) ? mode : "map"); } catch (_) { /* ignore */ }
}

/**
 * 후보 호 힌트(2026-09-14 "후보 호로 트리 깨우기") — 제안 신호에서 4자리 호를 뽑아 우선순위로 정렬한다.
 * 관세율표 명칭 일치 > 국내 결정례 > 우리 사무소 이력 > LLM. 힌트는 서버 라우터가 어느 트리를 열지 정하는 데만 쓰고
 * 사실로는 쓰지 않는다. 최대 6개.
 */
export function candidateHs4Hints({ proposal = null, tariffItems = [], tariffMatches = [], precedentHints = null }) {
  const cands = buildProposalCandidates({ proposal, tariffItems, tariffMatches, precedentHints });
  if (!cands.length) return [];
  const rank = (c) => (c.origin?.tariff ? 0 : c.origin?.precedent ? 1 : c.origin?.history ? 2 : 3);
  const sourceOf = (c) => (c.origin?.tariff ? "tariff" : c.origin?.precedent ? "precedent" : c.origin?.history ? "history" : "llm");
  const seen = new Set(); const out = [];
  [...cands].sort((a, b) => rank(a) - rank(b) || b.support - a.support).forEach((c) => {
    const hs4 = String(c.code || "").slice(0, 4);
    if (hs4.length < 4 || seen.has(hs4)) return;
    seen.add(hs4); out.push({ hs4, source: sourceOf(c) });
  });
  return out.slice(0, 6);
}
